/**
 * Transforms My Performance Dashboard API responses into IMyPerformanceDashboardData.
 * Follows MyPerformance_UI.md and MyPerformance_API.md. All values from API; no hardcoded data.
 */

import type {
  IMyPerformanceDashboardData,
  IMetricChartConfig,
  ICancelationItem,
  IProgressMetric,
  ICriticalTask,
  ICandidateCard,
  IPipelineStage,
  IPipelineAlert,
  IStackedBarDataPoint,
} from './MyPerformanceDashboard.types';

const MONTH_ABBREV: Record<string, string> = {
  Jan: 'Jan', Feb: 'Feb', Mar: 'Mar', Apr: 'Apr', May: 'May', Jun: 'Jun',
  Jul: 'Jul', Aug: 'Aug', Sep: 'Sep', Oct: 'Oct', Nov: 'Nov', Dec: 'Dec',
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

const WIDGET_DATA_KEYS: Record<string, string[]> = {
  TOA: ['toaByMonth', 'data', 'Data'],
  GM: ['grossMarginByMonth', 'data', 'Data'],
  Retention: ['retentionByMonth', 'data', 'Data'],
  Cancellations: ['cancellations', 'data', 'Data'],
  ProgressBars: ['progressBars', 'data', 'Data'],
  CriticalTasks: ['criticalTasks', 'data', 'Data'],
  CandidateCounts: ['candidateCounts', 'data', 'Data'],
  PipelineAlerts: ['pipelineAlerts', 'data', 'Data'],
  Pipeline: ['pipeline', 'data', 'Data'],
};

function toArr(obj: unknown, widgetKeys?: string[]): any[] {
  if (Array.isArray(obj)) return obj;
  if (!obj || typeof obj !== 'object') return [];
  const o = obj as Record<string, unknown>;
  const keys = widgetKeys ?? ['data', 'Data', 'items', 'Items'];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = toArr(v, widgetKeys);
      if (inner.length) return inner;
      return [v];
    }
  }
  return [];
}

function val<T>(row: any, ...keys: string[]): T | undefined {
  if (!row || typeof row !== 'object') return undefined;
  for (const k of keys) {
    const v = row?.[k] ?? row?.[k.charAt(0).toLowerCase() + k.slice(1)];
    if (v !== undefined && v !== null) return v as T;
  }
  return undefined;
}

function getNum(o: any, ...keys: string[]): number {
  const v = val<number | string>(o, ...keys);
  if (v === undefined || v === null) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function getStr(o: any, ...keys: string[]): string {
  const v = val<string>(o, ...keys);
  return v != null ? String(v) : '';
}

function formatMonth(monthRaw: string, fallbackIndex: number): string {
  if (!monthRaw) return MONTH_ABBREV[String(fallbackIndex + 1)] || `M${fallbackIndex + 1}`;
  const s = String(monthRaw);
  if (/^\d{4}-\d{2}/.test(s)) {
    const m = parseInt(s.slice(5, 7), 10);
    return MONTH_ABBREV[m] || MONTH_ABBREV[String(m)] || s.slice(0, 3);
  }
  return s.length >= 3 ? s.slice(0, 3) : s;
}

const MONTH_KEYS = ['Month', 'month', 'ReportMonth', 'reportMonth', 'Period', 'period'];
const LABEL_KEYS = ['Label', 'label', 'Name', 'name', 'Title', 'title'];
const COLOR_KEYS = ['Color', 'color', 'HexColor', 'hexColor'];

/** TOA: All from API - barValue=ToaValue, current/prev/trend/target from any row. */
export function transformToa(rows: any[]): IMetricChartConfig {
  const arr = toArr(rows, WIDGET_DATA_KEYS.TOA);
  const r = arr[0] || {};
  const data: IStackedBarDataPoint[] = [];
  const topLabels: string[] = [];

  arr.forEach((row, i) => {
    const v = getNum(row, 'ToaValue', 'toaValue', 'Value', 'value');
    const monthRaw = getStr(row, ...MONTH_KEYS);
    const month = formatMonth(monthRaw, i);
    data.push({ month, s1: v * 0.5, s2: v * 0.35, s3: v * 0.15 });
    topLabels.push(String(v));
  });

  const currentVal = getNum(r, 'CurrentValue', 'currentValue') || (arr.length ? getNum(arr[arr.length - 1], 'ToaValue', 'toaValue') : 0);
  const trend = getNum(r, 'TrendPercent', 'trendPercent');
  const target = getNum(r, 'Target', 'target');

  const hasData = data.length > 0;
  return {
    title: getStr(r, ...LABEL_KEYS) || 'TOA',
    data: hasData ? data : [{ month: '—', s1: 0, s2: 0, s3: 0 }],
    topLabels: hasData ? topLabels : ['0'],
    barColors: ['#5a9fd4', '#8ab8e8', '#d0d0d0'],
    target: target ? String(target) : '',
    current: String(Math.round(currentVal)),
    changePercent: `${trend >= 0 ? '+' : ''}${Math.round(trend)}%`,
    changeUp: trend >= 0,
  };
}

/** GM: All from API. */
export function transformGm(rows: any[]): IMetricChartConfig {
  const arr = toArr(rows, WIDGET_DATA_KEYS.GM);
  const r = arr[0] || {};
  const data: IStackedBarDataPoint[] = [];
  const topLabels: string[] = [];

  arr.forEach((row, i) => {
    const v = getNum(row, 'GrossMarginPercent', 'grossMarginPercent', 'Value', 'value');
    const monthRaw = getStr(row, ...MONTH_KEYS);
    const month = formatMonth(monthRaw, i);
    data.push({ month, s1: v * 0.4, s2: v * 0.35, s3: v * 0.25 });
    topLabels.push(`${v}%`);
  });

  const currentVal = getNum(r, 'CurrentValue', 'currentValue') || (arr.length ? getNum(arr[arr.length - 1], 'GrossMarginPercent', 'grossMarginPercent') : 0);
  const trend = getNum(r, 'TrendPercent', 'trendPercent');
  const target = getNum(r, 'Target', 'target');

  return {
    title: getStr(r, ...LABEL_KEYS) || 'GM',
    data: data.length ? data : [{ month: '—', s1: 0, s2: 0, s3: 0 }],
    topLabels: topLabels.length ? topLabels : ['0%'],
    barColors: ['#c5dcf0', '#8ab8e8', '#5a9fd4'],
    target: target ? `${target}%` : '',
    current: String(currentVal),
    changePercent: `${trend >= 0 ? '+' : ''}${Math.round(trend)}%`,
    changeUp: trend >= 0,
  };
}

/** Retention: All from API. */
export function transformRetention(rows: any[]): IMetricChartConfig {
  const arr = toArr(rows, WIDGET_DATA_KEYS.Retention);
  const r = arr[0] || {};
  const data: IStackedBarDataPoint[] = [];
  const topLabels: string[] = [];

  arr.forEach((row, i) => {
    const v = getNum(row, 'RetentionPercent', 'retentionPercent', 'Value', 'value');
    const monthRaw = getStr(row, ...MONTH_KEYS);
    const month = formatMonth(monthRaw, i);
    data.push({ month, s1: v * 0.4, s2: v * 0.35, s3: v * 0.25 });
    topLabels.push(`${v}%`);
  });

  const currentVal = getNum(r, 'CurrentValue', 'currentValue') || (arr.length ? getNum(arr[arr.length - 1], 'RetentionPercent', 'retentionPercent') : 0);
  const trend = getNum(r, 'TrendPercent', 'trendPercent');
  const target = getNum(r, 'Target', 'target');

  return {
    title: getStr(r, ...LABEL_KEYS) || 'Retention',
    data: data.length ? data : [{ month: '—', s1: 0, s2: 0, s3: 0 }],
    topLabels: topLabels.length ? topLabels : ['0%'],
    barColors: ['#c8e6c9', '#81c784', '#4caf50'],
    target: target ? `${target}%` : '',
    current: String(currentVal),
    changePercent: `${trend >= 0 ? '+' : ''}${Math.round(trend)}%`,
    changeUp: trend >= 0,
  };
}

/** Cancellations: segmentPct = (count/TotalPlacements)*100, center = (total/TotalPlacements)*100. 28=Travel, 29=Facility, 30=AMN. */
const CANCELLATION_COLORS: Record<number, string> = {
  28: '#3a3a8a',
  29: '#e8983a',
  30: '#9b8fd4',
};

export function transformCancellations(rows: any[]): { data: ICancelationItem[]; centerValue: string } {
  const arr = toArr(rows, WIDGET_DATA_KEYS.Cancellations);
  const totalPlacements = getNum(arr[0], 'TotalPlacements', 'totalPlacements');
  const totalCancellations = arr.reduce((s, r) => s + getNum(r, 'CancellationCount', 'cancellationCount', 'Value', 'value', 'Count', 'count'), 0);

  const items: ICancelationItem[] = arr.map((r) => {
    const count = getNum(r, 'CancellationCount', 'cancellationCount', 'Value', 'value', 'Count', 'count');
    const reasonId = getNum(r, 'OrderDecrementReasonId', 'orderDecrementReasonId');
    const name = getStr(r, 'CancellationReason', 'cancellationReason', ...LABEL_KEYS) || getStr(r, 'Reason', 'reason') || '—';
    const color = getStr(r, ...COLOR_KEYS) || CANCELLATION_COLORS[reasonId] || '#999';
    const segmentPct = totalPlacements > 0 ? (count / totalPlacements) * 100 : 0;
    return { name, value: Math.round(segmentPct * 10) / 10, color };
  });

  const center = totalPlacements > 0 ? (totalCancellations / totalPlacements) * 100 : 0;

  return {
    data: items,
    centerValue: `${Math.round(center)}%`,
  };
}

/** ProgressBars: callsFill=(CallsMade/30)*100, target 30 from config. */
const CALLS_TARGET = 30;

export function transformProgressBars(rows: any[]): IProgressMetric[] {
  const arr = toArr(rows, WIDGET_DATA_KEYS.ProgressBars);
  const r = arr[0] || {};

  const callsMade = getNum(r, 'CallsMade', 'callsMade');
  const targetCalls = getNum(r, 'CallsTarget', 'callsTarget') || CALLS_TARGET;
  const netWeeksBooked = getNum(r, 'NetWeeksBooked', 'netWeeksBooked');
  const tasksCompleted = getNum(r, 'TasksCompleted', 'tasksCompleted');
  const tasksTotal = getNum(r, 'TasksTotal', 'tasksTotal') || 1;

  const callsFillPct = Math.min(100, (callsMade / targetCalls) * 100);
  const tasksFillPct = tasksTotal > 0 ? (tasksCompleted / tasksTotal) * 100 : 0;

  return [
    { label: 'Calls Made', value: `${callsMade}/${targetCalls}`, pct: callsFillPct, color: '#d32f2f' },
    { label: 'Net Weeks Booked', value: String(netWeeksBooked), pct: 100, color: '#1565c0' },
    { label: 'My Tasks', value: `${tasksCompleted}/${tasksTotal}`, pct: tasksFillPct, color: '#1565c0' },
  ];
}

/** CriticalTasks: 4 cards per design. Map CriticalTaskType to label. Show all including count=0. */
const CRITICAL_TASK_ORDER: [string, string][] = [
  ['DeclinedByTraveler', 'REJECTED BY TRAVELER'],
  ['DeclinedByFacility', 'REJECTED BY FACILITY'],
  ['RequestCancelled', 'REQUEST CANCELLED'],
  ['TotalRejected', 'REJECTED CANDIDATES'],
];

export function transformCriticalTasks(rows: any[]): ICriticalTask[] {
  const arr = toArr(rows, WIDGET_DATA_KEYS.CriticalTasks);
  const byType: Record<string, number> = {};
  arr.forEach((r) => {
    const type = getStr(r, 'CriticalTaskType', 'criticalTaskType', ...LABEL_KEYS).trim() || '';
    const count = getNum(r, 'RejectedCount', 'rejectedCount', 'Count', 'count', 'Value', 'value');
    byType[type] = count;
  });
  return CRITICAL_TASK_ORDER.map(([type, label]) => ({
    count: byType[type] ?? 0,
    label,
  }));
}

/** CandidateCounts: Order per UI doc. New Job Match = 0 (gap). */
const CANDIDATE_MAPPING: [string, string][] = [
  ['NewlyAssigned', 'Newly Assigned'],
  ['NewJobMatch', 'New Job Match'],
  ['ApplicationWeek', 'New Job Interest'],
  ['WithOffers', 'With Offers'],
  ['WithExtensions', 'With Extensions'],
  ['WithRebook', 'With Rebook'],
  ['CallBackWeek', 'Call Back'],
];

export function transformCandidateCounts(rows: any[]): ICandidateCard[] {
  const arr = toArr(rows, WIDGET_DATA_KEYS.CandidateCounts);
  const r = arr[0] || {};
  const obj = r && typeof r === 'object' ? r : {};
  const cards: ICandidateCard[] = [];

  CANDIDATE_MAPPING.forEach(([key, label]) => {
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    let count = getNum(obj, key, camel);
    if (key === 'NewJobMatch') count = 0;
    cards.push({ count, label });
  });

  return cards;
}

/** PipelineAlerts: 4 cards. */
const PIPELINE_ALERT_MAP: [string, string][] = [
  ['CredentialIssues', 'Credential Issues'],
  ['ExtensionOffers', 'Extension Offers'],
  ['ExpiresIn30Days', 'Expires in 30 Days'],
  ['HasNewComments', 'Has New Comments'],
];

export function transformPipelineAlerts(rows: any[]): IPipelineAlert[] {
  const arr = toArr(rows, WIDGET_DATA_KEYS.PipelineAlerts);
  const r = arr[0] || {};
  const colors = ['#b71c1c', '#e8883a', '#4caf50', '#1976d2'];

  return PIPELINE_ALERT_MAP.map(([pascal, label], i) => {
    const camel = pascal.charAt(0).toLowerCase() + pascal.slice(1);
    return { count: getNum(r, pascal, camel), label, color: colors[i] || '#999' };
  });
}

/**
 * Pipeline: API returns { candidateCount, slots }.
 * Each slot: { activityTypeID, activityTypeName, stages: [{ candidateCount, ... }] }.
 * Sum stages[].candidateCount per slot. segmentPct = (slotCount / candidateCount) * 100
 */
const PIPELINE_SLOT_COLORS: Record<number, string> = {
  1: '#e91e63',
  3: '#9c27b0',
  4: '#1565c0',
  5: '#4caf50',
};

export function transformPipeline(pipelineData: any): {
  stages: IPipelineStage[];
  donutData: { name: string; value: number; color: string }[];
  centerValue: string;
} {
  const data = pipelineData?.pipeline ?? pipelineData;
  const total = getNum(data, 'CandidateCount', 'candidateCount', 'Total', 'total');
  const slotsArr = toArr(data?.slots ?? data?.Slots ?? []);

  const stages: IPipelineStage[] = [];
  const donutData: { name: string; value: number; color: string }[] = [];

  slotsArr.forEach((slot: any) => {
    const typeId = getNum(slot, 'ActivityTypeID', 'activityTypeId', 'ActivityTypeId');
    const slotName =
      getStr(slot, 'ActivityTypeName', 'activityTypeName', 'Name', 'name') ||
      (typeId === 1 ? 'File Out' : typeId === 3 ? 'Offer' : typeId === 4 ? 'Booking' : typeId === 5 ? 'Assignment' : `Slot ${typeId}`);
    const stagesArr = toArr(slot?.stages ?? slot?.Stages ?? []);
    let slotCount = 0;
    stagesArr.forEach((s: any) => {
      slotCount += getNum(s, 'CandidateCount', 'candidateCount', 'PlacementCount', 'placementCount');
    });
    const segmentPct = total > 0 ? (slotCount / total) * 100 : 0;
    const color = getStr(slot, 'Color', 'color') || PIPELINE_SLOT_COLORS[typeId] || '#999';
    stages.push({ name: slotName, value: slotCount, color });
    donutData.push({ name: slotName, value: Math.round(segmentPct * 10) / 10, color });
  });

  return { stages, donutData, centerValue: `${total} CANDIDATES` };
}

const EMPTY_PROGRESS: IProgressMetric[] = [
  { label: 'Calls Made', value: '0/30', pct: 0, color: '#d32f2f' },
  { label: 'Net Weeks Booked', value: '0', pct: 100, color: '#1565c0' },
  { label: 'My Tasks', value: '0/1', pct: 0, color: '#1565c0' },
];

const emptyChartConfig = (title: string): IMetricChartConfig => ({
  title,
  data: [{ month: '—', s1: 0, s2: 0, s3: 0 }],
  topLabels: ['0'],
  barColors: ['#ddd', '#ddd', '#ddd'] as [string, string, string],
  target: '',
  current: '0',
  changePercent: '0%',
  changeUp: false,
});

export function buildDashboardFromApiResponses(responses: Record<string, any>): IMyPerformanceDashboardData {
  const chartConfigs: IMetricChartConfig[] = [
    transformToa(responses.TOA),
    transformGm(responses.GM),
    transformRetention(responses.Retention),
  ];

  const cancel = transformCancellations(responses.Cancellations ?? []);
  const progressBars = transformProgressBars(responses.ProgressBars ?? []);
  const criticalTasks = transformCriticalTasks(responses.CriticalTasks ?? []);
  const candidateCards = transformCandidateCounts(responses.CandidateCounts ?? []);
  const pipelineAlerts = transformPipelineAlerts(responses.PipelineAlerts ?? []);

  const pipeResp = responses.Pipeline;
  const pipeline = transformPipeline(pipeResp ?? {});

  const split = Math.ceil(candidateCards.length / 2);
  const candidatesRow1 = candidateCards.slice(0, split);
  const candidatesRow2 = candidateCards.slice(split);

  return {
    chartConfigs,
    cancelationData: cancel.data,
    cancelationCenterValue: cancel.centerValue,
    sidePanelMetrics: progressBars.length ? progressBars : EMPTY_PROGRESS,
    criticalTasksData: criticalTasks.length ? criticalTasks : CRITICAL_TASK_ORDER.map(([, label]) => ({ count: 0, label })),
    candidatesRow1,
    candidatesRow2,
    pipelineStages: pipeline.stages,
    pipelineDonutData: pipeline.donutData,
    pipelineCenterValue: pipeline.centerValue,
    pipelineAlerts,
  };
}

export function getEmptyDashboardData(): IMyPerformanceDashboardData {
  const emptyCandidates = CANDIDATE_MAPPING.map(([, label]) => ({ count: 0, label }));
  const split = Math.ceil(emptyCandidates.length / 2);
  return {
    chartConfigs: ['TOA', 'GM', 'Retention'].map(emptyChartConfig),
    cancelationData: [],
    cancelationCenterValue: '0%',
    sidePanelMetrics: EMPTY_PROGRESS,
    criticalTasksData: CRITICAL_TASK_ORDER.map(([, label]) => ({ count: 0, label })),
    candidatesRow1: emptyCandidates.slice(0, split),
    candidatesRow2: emptyCandidates.slice(split),
    pipelineStages: [],
    pipelineDonutData: [],
    pipelineCenterValue: '0 CANDIDATES',
    pipelineAlerts: PIPELINE_ALERT_MAP.map(([, label], i) => ({
      count: 0,
      label,
      color: ['#b71c1c', '#e8883a', '#4caf50', '#1976d2'][i] || '#999',
    })),
  };
}
